import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDictionary } from '@/lib/i18n'
import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

import { BIKE_IMAGES, GALLERY_ORDER } from '@/constants/bike'

const pad = (value: number): string => String(value).padStart(2, '0')

const CONTROL_CLASSES = 'inline-flex size-11 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:border-white/50 hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'

const GallerySection: FC = (): ReactNode => {
    const t = useDictionary()
    const { gallery } = t
    const [index, setIndex] = useState(0)

    const total = GALLERY_ORDER.length
    const activeKey = GALLERY_ORDER[index]
    const active = BIKE_IMAGES[activeKey]

    const go = (next: number): void => setIndex((next + total) % total)

    return (
        <section id='gallery' aria-labelledby='gallery-title' className='relative border-t border-border py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='flex flex-col gap-8 md:flex-row md:items-end md:justify-between'>
                    <Reveal>
                        <SectionHeading id='gallery-title' eyebrow={gallery.eyebrow} title={gallery.title} />
                    </Reveal>

                    <div className='flex items-center gap-3'>
                        <p aria-live='polite' className='mr-2 font-mono text-sm tabular-nums text-muted-foreground'>
                            <span className='text-foreground'>{pad(index + 1)}</span> / {pad(total)}
                        </p>

                        <button type='button' onClick={() => go(index - 1)} aria-label={gallery.previous} className={CONTROL_CLASSES}>
                            <ChevronLeft aria-hidden='true' className='size-5' />
                        </button>

                        <button type='button' onClick={() => go(index + 1)} aria-label={gallery.next} className={CONTROL_CLASSES}>
                            <ChevronRight aria-hidden='true' className='size-5' />
                        </button>
                    </div>
                </div>

                <Reveal className='mt-10'>
                    <figure>
                        <div
                            className={cn(
                                'relative aspect-[4/3] overflow-hidden rounded-3xl border border-border transition-colors duration-500 sm:aspect-[16/9]',
                                active.studio ? 'bg-[#efefef]' : 'bg-[radial-gradient(ellipse_at_50%_58%,oklch(0.29_0.1_25),oklch(0.16_0.006_286)_70%)]'
                            )}
                        >
                            <Image
                                key={activeKey}
                                src={active.src}
                                fill
                                alt={gallery.items[activeKey]}
                                sizes='(min-width: 1280px) 1216px, 100vw'
                                className={cn(
                                    'animate-in fade-in duration-500 motion-reduce:animate-none',
                                    active.studio ? 'object-cover' : 'object-contain p-4 sm:p-10'
                                )}
                            />
                        </div>

                        <figcaption className='mt-4 flex items-center gap-3 text-sm text-muted-foreground'>
                            <span aria-hidden='true' className='h-px w-6 bg-race' />
                            {gallery.items[activeKey]}
                        </figcaption>
                    </figure>
                </Reveal>

                <div role='group' aria-label={gallery.thumbnails} className='mt-6 grid grid-cols-3 gap-3 sm:grid-cols-6'>
                    {GALLERY_ORDER.map((key, thumbIndex) => {
                        const thumb = BIKE_IMAGES[key]
                        const selected = thumbIndex === index

                        return (
                            <button
                                key={key}
                                type='button'
                                onClick={() => setIndex(thumbIndex)}
                                aria-pressed={selected}
                                aria-label={gallery.items[key]}
                                className={cn(
                                    'relative aspect-[4/3] overflow-hidden rounded-xl border transition duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                                    thumb.studio ? 'bg-[#efefef]' : 'bg-card',
                                    selected ? 'border-race ring-1 ring-race' : 'border-border opacity-60 hover:opacity-100'
                                )}
                            >
                                <Image
                                    src={thumb.src}
                                    fill
                                    alt=''
                                    sizes='(min-width: 640px) 200px, 33vw'
                                    className={thumb.studio ? 'object-cover' : 'object-contain p-1.5'}
                                />
                            </button>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}

export default GallerySection
